/**
 * BrandFinish monogram – a precise B/F ligature built from geometric primitives.
 *
 * Theme behaviour
 * ─────────────────
 *   `tone="auto"`           (default) — square inherits `var(--bf-fg)`,
 *                                       letter inherits `var(--bf-bg)`.
 *                                       In dark theme: porcelain square / ink letter.
 *                                       In light theme: ink square / mist letter.
 *
 *   `tone="light-on-dark"`  Forces porcelain square + graphite letter.
 *                            Use on always-dark surfaces (Login left panel,
 *                            About founder card, hero overlays).
 *
 *   `tone="dark-on-light"`  Forces ink square + porcelain letter.
 *                            Use on always-light surfaces (e.g. printed PDFs).
 *
 * The accent F cross-bar always uses `--bf-violet` for brand consistency.
 */
const TONES = {
  "light-on-dark": {
    square: "var(--bf-porcelain)",
    letter: "var(--bf-graphite)",
  },
  "dark-on-light": {
    square: "var(--bf-ink)",
    letter: "var(--bf-porcelain)",
  },
  auto: {
    // CSS variables that auto-invert with the active theme.
    square: "var(--bf-fg)",
    letter: "var(--bf-bg)",
  },
};

export default function BrandMonogram({
  size = 28,
  accent = "var(--bf-violet)",
  tone = "auto",
  className = "",
  testId = "brand-monogram",
}) {
  const { square, letter } = TONES[tone] || TONES.auto;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      data-testid={testId}
      data-tone={tone}
      role="img"
      aria-label="BrandFinish monogram"
    >
      <rect x="1" y="1" width="38" height="38" rx="8" fill={square} />
      {/* B – left – knocked out of the square */}
      <path
        d="M10 10h8.5c3.2 0 5.5 1.7 5.5 4.5 0 1.8-.9 3-2.3 3.6 1.9.6 3.1 2.1 3.1 4.2 0 3.1-2.4 4.9-6 4.9H10V10Zm3.2 2.8v4.5h4.9c1.6 0 2.6-.8 2.6-2.2 0-1.5-1-2.3-2.6-2.3h-4.9Zm0 7.3v4.9h5.3c1.9 0 3.1-.9 3.1-2.5s-1.2-2.4-3.1-2.4h-5.3Z"
        fill={letter}
      />
      {/* F – right – the accent cross-bar */}
      <rect x="26" y="10" width="4.2" height="17" rx="0.5" fill={accent} />
      <rect x="26" y="10" width="4.2" height="2.4" rx="0.5" fill={accent} />
      <rect x="26" y="17" width="3.2" height="2.2" rx="0.5" fill={accent} opacity="0.85" />
    </svg>
  );
}

export function WordmarkLockup({ compact = false, tone = "auto" }) {
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-wordmark">
      <BrandMonogram size={compact ? 24 : 28} tone={tone} />
      {!compact && (
        <div className="leading-tight">
          <div
            className="font-editorial text-[18px] tracking-[-0.01em]"
            style={{ color: "currentColor" }}
          >
            BrandFinish
          </div>
          <div className="text-[9px] uppercase tracking-[0.28em] font-mono" style={{ color: "var(--bf-fg-subtle)" }}>
            Studio
          </div>
        </div>
      )}
    </div>
  );
}
